// The client's copy of the world: applies delta snapshots and answers "what did everything look
// like at server time t?" by interpolating between the samples around t.
//
// Age rule: snapshots are stamped with server time, and the renderer asks for a time a little
// behind the estimated server clock, so an arriving snapshot is already "aged" by the clock
// rather than eased toward (easing toward a raw snapshot is easing toward the past).
// Extrapolation only happens when the stream itself has stalled, and is clamped.

import type { Entity } from "../contract.js";
import type { SnapshotMsg } from "../shared/protocol.js";

export const MAX_EXTRAPOLATE_MS = 400;
const HISTORY_MS = 1500;

interface Sample {
  t: number;
  f: Record<string, unknown>;
}

interface Known {
  id: number;
  type: string;
  owner?: string;
  samples: Sample[];
  latest: Record<string, unknown>;
}

export class Replica {
  private readonly known = new Map<number, Known>();
  readonly names = new Map<string, string>();
  you: number | undefined;
  players = 0;
  leaderboard: [string, number][] = [];
  lastSeq = 0;
  /** Server time of the newest snapshot applied. */
  lastSnapshotTime = -Infinity;
  snapshots = 0;

  apply(snap: SnapshotMsg): void {
    if (snap.seq <= this.lastSeq) return; // stale or duplicate
    this.lastSeq = snap.seq;
    this.lastSnapshotTime = snap.time;
    this.snapshots++;

    if (snap.rm) for (const id of snap.rm) this.known.delete(id);

    if (snap.e) {
      for (const key in snap.e) {
        const id = Number(key);
        const patch = snap.e[key];
        let k = this.known.get(id);
        if (!k) {
          k = { id, type: String(patch._t ?? "?"), samples: [], latest: {} };
          if (typeof patch._o === "string") k.owner = patch._o;
          this.known.set(id, k);
        }
        const merged: Record<string, unknown> = { ...k.latest };
        for (const f in patch) if (f.charCodeAt(0) !== 95 /* _ */) merged[f] = patch[f];
        k.latest = merged;
        k.samples.push({ t: snap.time, f: merged });
        // Keep a short history: enough to interpolate across jitter, not enough to leak.
        const cutoff = snap.time - HISTORY_MS;
        let drop = 0;
        while (drop < k.samples.length - 2 && k.samples[drop + 1].t < cutoff) drop++;
        if (drop) k.samples.splice(0, drop);
      }
    }

    if (snap.pn) {
      for (const pid in snap.pn) {
        const name = snap.pn[pid];
        if (name === null) this.names.delete(pid);
        else this.names.set(pid, name);
      }
    }
    this.you = snap.you;
    this.players = snap.n;
    if (snap.lb) this.leaderboard = snap.lb;
  }

  get size(): number {
    return this.known.size;
  }

  name(playerId: string | undefined): string {
    return (playerId !== undefined && this.names.get(playerId)) || "?";
  }

  /**
   * Every known entity as it looked at server time `t` (ms). Numeric fields are interpolated;
   * other fields take the value from the sample at or before `t`.
   */
  sample(t: number): { entities: Entity[]; alpha: number } {
    const out: Entity[] = [];
    let alphaSum = 0;
    for (const k of this.known.values()) {
      const s = k.samples;
      const n = s.length;
      if (n === 0) continue;
      let fields: Record<string, unknown>;
      let alpha = 0;
      if (t <= s[0].t) {
        fields = s[0].f;
      } else if (t >= s[n - 1].t) {
        const last = s[n - 1];
        const stalled = t > this.lastSnapshotTime;
        if (stalled && n >= 2 && last.t >= this.lastSnapshotTime) {
          // Stream stalled while this entity was moving: carry its velocity forward, briefly.
          const prev = s[n - 2];
          const span = last.t - prev.t;
          const ahead = Math.min(t - last.t, MAX_EXTRAPOLATE_MS);
          fields = span > 0 ? lerpFields(prev.f, last.f, 1 + ahead / span) : last.f;
          alpha = 1;
        } else {
          // Either newer snapshots said nothing about it (so it is still), or we have no velocity.
          fields = last.f;
          alpha = 1;
        }
      } else {
        let i = n - 2;
        while (i > 0 && s[i].t > t) i--;
        const a = s[i];
        const b = s[i + 1];
        alpha = (t - a.t) / (b.t - a.t);
        fields = lerpFields(a.f, b.f, alpha);
      }
      const ent = { ...fields, id: k.id, type: k.type } as Entity;
      if (k.owner !== undefined) ent.owner = k.owner;
      out.push(ent);
      alphaSum += alpha;
    }
    return { entities: out, alpha: out.length ? alphaSum / out.length : 0 };
  }
}

function lerpFields(a: Record<string, unknown>, b: Record<string, unknown>, k: number): Record<string, unknown> {
  const out: Record<string, unknown> = { ...b };
  for (const f in b) {
    const va = a[f];
    const vb = b[f];
    if (typeof va === "number" && typeof vb === "number") out[f] = va + (vb - va) * k;
    else if (k < 1 && va !== undefined) out[f] = va; // discrete fields switch at the later sample
  }
  return out;
}
